/**
 * GRID-9000 and its siblings: house bots that fill empty slots so one person can play.
 * Survival heuristic, not a planner: keep the most open space ahead, never drive into a wall
 * when there is any other option, and wander a little so the lines aren't machine-perfect.
 */
import { CPU_ID_PREFIX } from '../duel/cpu'
import type { PlayerPublic } from '../protocol'
import {
  ARENA_CELLS,
  ARENA_W,
  type CycleSnapshot,
  cellIndex,
  DX,
  DY,
  type Heading,
  inBounds,
  type RoundState,
  turnLeft,
  turnRight,
} from './types'

export const CYCLE_BOTS: readonly PlayerPublic[] = [
  { id: `${CPU_ID_PREFIX}grid-9000`, nickname: 'GRID-9000', avatar: '🤖', color: 'amber' },
  { id: `${CPU_ID_PREFIX}grid-9001`, nickname: 'GRID-9001', avatar: '🤖', color: 'sky' },
  { id: `${CPU_ID_PREFIX}grid-9002`, nickname: 'GRID-9002', avatar: '🤖', color: 'violet' },
]

/** How much open area a bot bothers to measure per option; beyond this every option looks fine. */
const FLOOD_CAP = 160
/** Chance per tick of a random safe move instead of the best one. */
const BOT_WANDER = 0.04
/** Racing another head for the same cell is a coin flip at best: treat it as nearly fatal. */
const HEAD_ON_PENALTY = 1_000

/** Cells reachable from `start` over empty cells, counted up to `FLOOD_CAP`. */
function floodArea(cells: Uint8Array, start: number, scratch: Uint8Array): number {
  scratch.fill(0)
  const queue = [start]
  scratch[start] = 1
  let count = 0
  while (queue.length > 0 && count < FLOOD_CAP) {
    const idx = queue.shift()!
    count++
    const x = idx % ARENA_W
    const y = (idx - x) / ARENA_W
    for (let h = 0; h < 4; h++) {
      const nx = x + DX[h]!
      const ny = y + DY[h]!
      if (!inBounds(nx, ny)) continue
      const ni = cellIndex(nx, ny)
      if (cells[ni] !== 0 || scratch[ni]) continue
      scratch[ni] = 1
      queue.push(ni)
    }
  }
  return count
}

function targetOf(c: CycleSnapshot, h: Heading): number {
  const x = c.x + DX[h]!
  const y = c.y + DY[h]!
  return inBounds(x, y) ? cellIndex(x, y) : -1
}

/** The heading the bot at `index` drives next; straight ahead when every option is fatal. */
export function chooseBotHeading(round: RoundState, index: number, rng: () => number): Heading {
  const me = round.cycles[index]!
  const options: Heading[] = [me.heading, turnLeft(me.heading), turnRight(me.heading)]
  const rivals = new Set<number>()
  round.cycles.forEach((c, i) => {
    if (i !== index && c.alive) rivals.add(targetOf(c, c.heading))
  })
  const scratch = new Uint8Array(ARENA_CELLS)
  let best: Heading = me.heading
  let bestScore = Number.NEGATIVE_INFINITY
  const safe: Heading[] = []
  for (const h of options) {
    const idx = targetOf(me, h)
    if (idx < 0 || round.cells[idx] !== 0) continue
    safe.push(h)
    const score = floodArea(round.cells, idx, scratch) - (rivals.has(idx) ? HEAD_ON_PENALTY : 0)
    if (score > bestScore) {
      bestScore = score
      best = h
    }
  }
  if (safe.length > 1 && rng() < BOT_WANDER) return safe[Math.floor(rng() * safe.length)]!
  return best
}
